import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/messageSearch.css';

/**
 * Message Search Component
 * Provides an advanced search interface for conversations and messages
 * with suggestions, filters, and keyboard navigation
 */
const MessageSearch = ({
    query,
    setQuery,
    suggestions = [],
    searchHistory = [],
    showSuggestions,
    setShowSuggestions,
    onSuggestionSelect,
    clearHistory,
    placeholder = "Search conversations, participants, or products...",
    className = ""
}) => {
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isFocused, setIsFocused] = useState(false);
    const searchInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const navigate = useNavigate();

    // Combined suggestions (history + generated suggestions)
    const combinedSuggestions = React.useMemo(() => {
        const items = [];
        
        // Add search history if no query or query matches history
        if (query.length < 2 && searchHistory.length > 0) {
            items.push({
                type: 'history-header',
                label: 'Recherches récentes',
                id: 'history-header'
            });
            
            searchHistory.slice(0, 5).forEach((item, index) => {
                items.push({
                    type: 'history',
                    label: item,
                    id: `history-${index}`,
                    icon: '🕒'
                });
            });
        }
        
        // Add generated suggestions
        if (suggestions.length > 0 && query.length >= 2) {
            if (items.length > 0) {
                items.push({
                    type: 'divider',
                    id: 'divider'
                });
            }
            
            items.push({
                type: 'suggestions-header',
                label: 'Suggestions',
                id: 'suggestions-header'
            });
            
            suggestions.forEach((suggestion, index) => {
                const isUsername = suggestion.startsWith('@');
                items.push({
                    type: 'suggestion',
                    label: suggestion,
                    id: `suggestion-${index}`,
                    icon: isUsername ? '👤' : '🔍'
                });
            });
        }
        
        return items;
    }, [suggestions, searchHistory, query]);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        const selectableItems = combinedSuggestions.filter(
            item => item.type === 'history' || item.type === 'suggestion'
        );
        
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!showSuggestions && (suggestions.length > 0 || searchHistory.length > 0)) {
                    setShowSuggestions(true);
                    setSelectedIndex(0);
                } else if (selectedIndex < selectableItems.length - 1) {
                    setSelectedIndex(selectedIndex + 1);
                }
                break;
                
            case 'ArrowUp':
                e.preventDefault();
                if (selectedIndex > 0) {
                    setSelectedIndex(selectedIndex - 1);
                } else if (selectedIndex === 0) {
                    setSelectedIndex(-1);
                }
                break;
                
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectableItems[selectedIndex]) {
                    handleSuggestionClick(selectableItems[selectedIndex]);
                } else if (query.trim()) {
                    handleSearch();
                }
                break;
                
            case 'Escape':
                setShowSuggestions(false);
                setSelectedIndex(-1);
                searchInputRef.current?.blur();
                break;
                
            default:
                break;
        }
    };

    // Handle suggestion selection
    const handleSuggestionClick = (suggestion) => {
        if (suggestion.type === 'history' || suggestion.type === 'suggestion') {
            setQuery(suggestion.label);
            setShowSuggestions(false);
            setSelectedIndex(-1);
            
            if (onSuggestionSelect) {
                onSuggestionSelect(suggestion.label);
            }
            
            // Focus back on input after selection
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    // Handle search execution
    const handleSearch = () => {
        if (query.trim()) {
            setShowSuggestions(false);
            setSelectedIndex(-1);
            
            if (onSuggestionSelect) {
                onSuggestionSelect(query.trim());
            }
        }
    };

    // Clear search
    const clearSearch = () => {
        setQuery('');
        setShowSuggestions(false);
        setSelectedIndex(-1);
        searchInputRef.current?.focus();
    };

    // Handle input focus
    const handleFocus = () => {
        setIsFocused(true);
        if (suggestions.length > 0 || searchHistory.length > 0) {
            setShowSuggestions(true);
        }
    };

    // Handle input blur (with delay to allow clicks)
    const handleBlur = () => {
        setIsFocused(false);
        setTimeout(() => {
            setShowSuggestions(false);
            setSelectedIndex(-1);
        }, 200);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        setSelectedIndex(-1);
        
        if (value.trim().length >= 2 || value.trim().length === 0) {
            setShowSuggestions(true);
        }
    };

    // Auto-scroll selected suggestion into view
    useEffect(() => {
        if (selectedIndex >= 0 && suggestionsRef.current) {
            const selectedElement = suggestionsRef.current.querySelector(
                `.message-search-suggestion:nth-child(${selectedIndex + 1})`
            );
            if (selectedElement) {
                selectedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [selectedIndex]);

    return (
        <div className={`message-search-container ${className}`}>
            <div className={`message-search-input-wrapper ${isFocused ? 'focused' : ''}`}>
                <input
                    ref={searchInputRef}
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    className="message-search-input"
                    autoComplete="off"
                    spellCheck="false"
                />
                
                {query && (
                    <button
                        className="clear-search-button"
                        onClick={clearSearch}
                        type="button"
                        tabIndex={-1}
                        aria-label="Effacer la recherche"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Search Suggestions Dropdown */}
            {showSuggestions && combinedSuggestions.length > 0 && (
                <div className="message-search-suggestions" ref={suggestionsRef}>
                    {combinedSuggestions.map((item, index) => {
                        const selectableItems = combinedSuggestions.filter(
                            s => s.type === 'history' || s.type === 'suggestion'
                        );
                        const selectableIndex = selectableItems.findIndex(s => s.id === item.id);
                        const isSelected = selectedIndex === selectableIndex;

                        if (item.type === 'history-header' || item.type === 'suggestions-header') {
                            return (
                                <div key={item.id} className="suggestion-header">
                                    <span>{item.label}</span>
                                    {item.type === 'history-header' && searchHistory.length > 0 && (
                                        <button
                                            className="clear-history-button"
                                            onClick={clearHistory}
                                            type="button"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            );
                        }

                        if (item.type === 'divider') {
                            return <div key={item.id} className="suggestion-divider" />;
                        }

                        if (item.type === 'history' || item.type === 'suggestion') {
                            return (
                                <div
                                    key={item.id}
                                    className={`message-search-suggestion ${isSelected ? 'selected' : ''} ${item.type}`}
                                    onClick={() => handleSuggestionClick(item)}
                                    onMouseEnter={() => setSelectedIndex(selectableIndex)}
                                >
                                    <span className="suggestion-icon">{item.icon}</span>
                                    <span className="suggestion-text">{item.label}</span>
                                    {item.type === 'history' && (
                                        <span className="suggestion-type">Recent</span>
                                    )}
                                </div>
                            );
                        }

                        return null;
                    })}
                </div>
            )}
        </div>
    );
};

export default MessageSearch;
